import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./ReplicaDustCalculator.module.css";
import {
  calculateInventoryTotals,
  DUST_GOAL,
  dustPerCell,
  INVENTORY_CELL_CAPACITY,
  meetsRequiredDustPerCell,
  parseFoundCounts,
  REPLICA_UNIQUES,
  serializeFoundCounts,
  sortReplicaUniques,
  type ReplicaSortKey,
  type SortDirection,
} from "../lib/replica-uniques";

const dustFormatter = new Intl.NumberFormat();
const perCellFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
});

export const ReplicaDustCalculator: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFoundCounts = searchParams.get("found");
  const [sortKey, setSortKey] = useState<ReplicaSortKey>("dustValue");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [quantityInputs, setQuantityInputs] = useState<Record<string, string>>(
    () => parseFoundCounts(urlFoundCounts)
  );
  const lastUrlFoundCounts = useRef(urlFoundCounts);

  const sortedItems = useMemo(
    () => sortReplicaUniques(REPLICA_UNIQUES, sortKey, sortDirection),
    [sortKey, sortDirection]
  );
  const serializedFoundCounts = useMemo(
    () => serializeFoundCounts(quantityInputs),
    [quantityInputs]
  );
  const quantities = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(quantityInputs).map(([name, value]) => [name, Number(value)])
      ),
    [quantityInputs]
  );
  const totals = useMemo(
    () => calculateInventoryTotals(REPLICA_UNIQUES, quantities),
    [quantities]
  );

  useEffect(() => {
    // URL changes from browser back/forward should replace the local counts;
    // edits made here are mirrored back into the URL without adding history.
    if (urlFoundCounts !== lastUrlFoundCounts.current) {
      lastUrlFoundCounts.current = urlFoundCounts;
      setQuantityInputs(parseFoundCounts(urlFoundCounts));
      return;
    }
    if (serializedFoundCounts === urlFoundCounts) return;

    lastUrlFoundCounts.current = serializedFoundCounts;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (serializedFoundCounts === null) {
          next.delete("found");
        } else {
          next.set("found", serializedFoundCounts);
        }
        return next;
      },
      { replace: true }
    );
  }, [urlFoundCounts, serializedFoundCounts, setSearchParams]);

  const changeSort = (key: ReplicaSortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
      return;
    }
    setSortKey(key);
    setSortDirection("desc");
  };

  const changeQuantity = (name: string, value: string) => {
    if (value === "" || /^\d+$/.test(value)) {
      setQuantityInputs((previous) => ({ ...previous, [name]: value }));
    }
  };

  const sortHeader = (key: ReplicaSortKey, label: string) => (
    <th
      scope="col"
      aria-sort={
        sortKey === key
          ? sortDirection === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <button type="button" onClick={() => changeSort(key)}>
        {label}
        {sortKey === key && (
          <span aria-hidden="true"> {sortDirection === "desc" ? "▼" : "▲"}</span>
        )}
      </button>
    </th>
  );

  return (
    <main className={styles.container}>
      <h1>Replica Unique Dust Values</h1>
      <p className={styles.description}>
        Compare Thaumaturgic Dust values for Replica uniques, sorted by total
        dust or dust per inventory cell. Values assume an item level 84 item
        with 20% quality (Q20); cell counts use each item&apos;s specific base
        type. Found counts are saved in the URL so you can bookmark or share
        your inventory.
      </p>

      <section className={styles.summary} aria-label="Inventory and dust totals">
        <div className={styles.metrics}>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Inventory cells used</span>
            <strong className={styles.metricValue}>
              {dustFormatter.format(totals.cellsUsed)} / {INVENTORY_CELL_CAPACITY}
            </strong>
            <progress
              max={INVENTORY_CELL_CAPACITY}
              value={Math.min(totals.cellsUsed, INVENTORY_CELL_CAPACITY)}
              aria-label={`${totals.cellsUsed} of ${INVENTORY_CELL_CAPACITY} inventory cells used`}
            />
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Total dust</span>
            <strong className={styles.metricValue}>
              {dustFormatter.format(totals.dustTotal)} / {dustFormatter.format(DUST_GOAL)}
            </strong>
            <progress
              max={DUST_GOAL}
              value={Math.min(totals.dustTotal, DUST_GOAL)}
              aria-label={`${dustFormatter.format(totals.dustTotal)} of ${dustFormatter.format(DUST_GOAL)} dust`}
            />
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>
              Average dust needed per unused cell
            </span>
            <strong className={styles.metricValue}>
              {totals.requiredDustPerCell === null
                ? "Not possible"
                : perCellFormatter.format(totals.requiredDustPerCell)}
            </strong>
            <span className={styles.metricNote}>
              {totals.goalReached
                ? "10 million dust goal reached"
                : totals.requiredDustPerCell === null
                  ? "No cells left before reaching the goal"
                  : `${dustFormatter.format(totals.cellsRemaining)} cells remaining`}
            </span>
          </div>
        </div>
        <p className={styles.highlightLegend}>
          Dust / cell values highlighted green meet or exceed the required
          average; values in red are below it.
        </p>
      </section>

      <p className={styles.source}>
        Dust values are based on the{" "}
        <a href="https://github.com/deronek/poe-disenchant-tool/blob/main/data/dust/poe-dust.js">
          poe-disenchant-tool dust data
        </a>
        . Dust per cell is calculated as dust value divided by inventory cells.
      </p>

      <div className={styles.tableScroller}>
        <table className={styles.resultsTable}>
          <thead>
            <tr>
              <th scope="col">Unique</th>
              <th scope="col">Base type</th>
              <th scope="col" className={styles.numeric}>
                Cells
              </th>
              <th scope="col">Found</th>
              {sortHeader("dustValue", "Dust (Q20)")}
              {sortHeader("dustPerCell", "Dust / cell")}
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((item) => {
              const meetsCutoff = meetsRequiredDustPerCell(
                item,
                totals.requiredDustPerCell
              );
              return (
                <tr key={item.name}>
                  <td>{item.name}</td>
                  <td>{item.baseType}</td>
                  <td className={styles.numeric}>{item.cells}</td>
                  <td>
                    <input
                      className={styles.quantityInput}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="0"
                      value={quantityInputs[item.name] ?? ""}
                      onChange={(event) =>
                        changeQuantity(item.name, event.target.value)
                      }
                      aria-label={`Number of ${item.name} found`}
                    />
                  </td>
                  <td className={styles.numeric}>
                    {dustFormatter.format(item.dustValue)}
                  </td>
                  <td
                    className={`${styles.numeric} ${styles.perCellValue} ${
                      meetsCutoff ? styles.meetsCutoff : styles.belowCutoff
                    }`}
                    aria-label={`${perCellFormatter.format(dustPerCell(item))} dust per cell, ${meetsCutoff ? "meets or exceeds" : "below"} the required average`}
                  >
                    {perCellFormatter.format(dustPerCell(item))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={styles.itemCount}>{sortedItems.length} Replica uniques</p>
    </main>
  );
};
